// src/components/insurance/InsuranceForm.jsx
import React, { useState } from 'react';
import { ShieldCheck, Calendar, Car, User, CreditCard } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const InsuranceForm = () => {
  const { t } = useLanguage();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    // Insurance details
    duration: '30',
    vehicleType: 'car',
    
    // Vehicle details
    manufacturer: '',
    model: '',
    registrationNumber: '',
    vinCode: '',
    frameNumber: '',
    
    // Personal details
    firstName: '',
    lastName: '',
    idNumber: '',
    phone: '',
    email: '',
    
    // Payment details
    paymentMethod: 'card',
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Validate form data based on the current step
  const validateCurrentStep = () => {
    const errors = {};
    
    if (activeStep === 0) {
      // Insurance details step validation
      // (All fields here have default values, so no validation needed)
    } 
    else if (activeStep === 1) {
      // Vehicle details step validation
      if (!formData.manufacturer.trim()) errors.manufacturer = 'Manufacturer is required';
      if (!formData.model.trim()) errors.model = 'Model is required';
      if (!formData.registrationNumber.trim()) errors.registrationNumber = 'Registration number is required';
      if (!formData.vinCode && !formData.frameNumber) errors.identification = 'Either VIN code or frame number is required';
    } 
    else if (activeStep === 2) {
      // Personal details step validation
      if (!formData.firstName.trim()) errors.firstName = 'First name is required';
      if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
      if (!formData.idNumber.trim()) errors.idNumber = 'ID number is required';
      if (!formData.phone.trim()) errors.phone = 'Phone number is required';
      // Email is optional, no validation needed
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextStep = () => {
    const isValid = validateCurrentStep();
    if (isValid) {
      setActiveStep(prevStep => prevStep + 1);
    }
  };

  const handlePreviousStep = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Final validation before submission
    const isValid = validateCurrentStep();
    if (!isValid) return;
    
    // Here we'll just show an alert instead of submitting
    // In the future this will be replaced with actual payment processing
    alert('You will now be redirected to the payment page. This is where the payment integration will be implemented.');
    
    // We don't need to reset the form or advance to another step here
    // because the payment system will handle that in the future
  };

  // Fixed duration options with hardcoded text for Armenian
  const durationOptions = [
    { value: '15', label: '15 օր' },
    { value: '30', label: '30 օր' },
    { value: '90', label: '90 օր' },
    { value: '365', label: t('year') }
  ];

  const steps = [
    {
      title: t('insuranceDetails'),
      icon: Calendar,
      content: (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              {t('duration')}
            </label>
            <select
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              className="block w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {durationOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              {t('vehicleType')}
            </label>
            <select
              name="vehicleType"
              value={formData.vehicleType}
              onChange={handleChange}
              className="block w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="motorcycle">{t('motorcycle')}</option>
              <option value="car">{t('passengerCar')}</option>
              <option value="bus">{t('bus')}</option>
              <option value="truck">{t('truck')}</option>
              <option value="trailer">{t('trailer')}</option>
              <option value="special">{t('specialVehicle')}</option>
            </select>
          </div>
          
          <div className="bg-primary-50 p-4 rounded-md">
            <h4 className="font-medium text-primary-800 flex items-center">
              <ShieldCheck className="h-5 w-5 mr-2" />
              {t('insuranceCoverage')}
            </h4>
            <p className="mt-2 text-sm text-primary-700">
              {t('insuranceCoverageDesc')}
            </p>
            <div className="mt-3 flex items-center">
              <span className="text-xl font-bold text-primary-800">
                {formData.duration === "15" && "$50"}
                {formData.duration === "30" && "$80"}
                {formData.duration === "90" && "$200"}
                {formData.duration === "365" && "$500"}
              </span>
              <span className="ml-2 text-sm text-primary-600">
                {t('estimatedPrice')}
              </span>
            </div>
          </div>
        </div>
      )
    },
    {
      title: t('vehicleDetails'),
      icon: Car,
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                {t('manufacturer')} *
              </label>
              <input
                type="text"
                name="manufacturer"
                value={formData.manufacturer}
                onChange={handleChange}
                className={`block w-full px-3 py-2 border ${formErrors.manufacturer ? 'border-red-500' : 'border-neutral-300'} rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
              />
              {formErrors.manufacturer && (
                <p className="mt-1 text-sm text-red-600">{formErrors.manufacturer}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                {t('model')} *
              </label>
              <input
                type="text"
                name="model"
                value={formData.model}
                onChange={handleChange}
                className={`block w-full px-3 py-2 border ${formErrors.model ? 'border-red-500' : 'border-neutral-300'} rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
              />
              {formErrors.model && (
                <p className="mt-1 text-sm text-red-600">{formErrors.model}</p>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              {t('registrationNumber')} *
            </label>
            <input
              type="text"
              name="registrationNumber"
              value={formData.registrationNumber}
              onChange={handleChange}
              className={`block w-full px-3 py-2 border ${formErrors.registrationNumber ? 'border-red-500' : 'border-neutral-300'} rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
            />
            {formErrors.registrationNumber && (
              <p className="mt-1 text-sm text-red-600">{formErrors.registrationNumber}</p>
            )}
          </div>
          
          <div className={`border-t border-neutral-200 pt-4 ${formErrors.identification ? 'border-red-500' : ''}`}>
            {formErrors.identification && (
              <p className="mb-2 text-sm text-red-600">{formErrors.identification}</p>
            )}
            <div className="flex items-center mb-4">
              <input
                type="radio"
                id="vinCode"
                name="identificationMethod"
                value="vinCode"
                checked={formData.frameNumber === ''}
                onChange={() => setFormData(prev => ({ ...prev, frameNumber: '', vinCode: prev.vinCode || '' }))}
                className="mr-2"
              />
              <label htmlFor="vinCode" className="text-sm font-medium text-neutral-700">
                {t('vinCode')}
              </label>
            </div>
            {formData.frameNumber === '' && (
              <input
                type="text"
                name="vinCode"
                value={formData.vinCode}
                onChange={handleChange}
                placeholder="e.g. 1HGBH41JXMN109186"
                className="block w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-4"
              />
            )}
            
            <div className="flex items-center mb-4">
              <input
                type="radio"
                id="frameNumber"
                name="identificationMethod"
                value="frameNumber"
                checked={formData.vinCode === ''}
                onChange={() => setFormData(prev => ({ ...prev, vinCode: '', frameNumber: prev.frameNumber || '' }))}
                className="mr-2"
              />
              <label htmlFor="frameNumber" className="text-sm font-medium text-neutral-700">
                {t('frameNumber')}
              </label>
            </div>
            {formData.vinCode === '' && (
              <input
                type="text"
                name="frameNumber"
                value={formData.frameNumber}
                onChange={handleChange}
                placeholder="e.g. FR9876543210"
                className="block w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            )}
          </div>
        </div>
      )
    },
    {
      title: t('personalDetails'),
      icon: User,
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                {t('firstName')} *
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={`block w-full px-3 py-2 border ${formErrors.firstName ? 'border-red-500' : 'border-neutral-300'} rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
              />
              {formErrors.firstName && (
                <p className="mt-1 text-sm text-red-600">{formErrors.firstName}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                {t('lastName')} *
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={`block w-full px-3 py-2 border ${formErrors.lastName ? 'border-red-500' : 'border-neutral-300'} rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
              />
              {formErrors.lastName && (
                <p className="mt-1 text-sm text-red-600">{formErrors.lastName}</p>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              {t('idNumber')} *
            </label>
            <input
              type="text"
              name="idNumber"
              value={formData.idNumber}
              onChange={handleChange}
              className={`block w-full px-3 py-2 border ${formErrors.idNumber ? 'border-red-500' : 'border-neutral-300'} rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
            />
            {formErrors.idNumber && (
              <p className="mt-1 text-sm text-red-600">{formErrors.idNumber}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              {t('phone')} *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`block w-full px-3 py-2 border ${formErrors.phone ? 'border-red-500' : 'border-neutral-300'} rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
            />
            {formErrors.phone && (
              <p className="mt-1 text-sm text-red-600">{formErrors.phone}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              {t('email')}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="block w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      )
    },
    {
      title: t('review'),
      icon: CreditCard,
      content: (
        <div className="space-y-6">
          <div className="bg-neutral-50 p-4 rounded-md">
            <h4 className="font-medium text-neutral-900 border-b pb-2 mb-3">
              {t('insuranceDetails')}
            </h4>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
              <div className="col-span-1">
                <dt className="text-sm text-neutral-500">{t('duration')}</dt>
                <dd className="text-sm font-medium">
                  {durationOptions.find(opt => opt.value === formData.duration)?.label}
                </dd>
              </div>
              <div className="col-span-1">
                <dt className="text-sm text-neutral-500">{t('vehicleType')}</dt>
                <dd className="text-sm font-medium">
                  {t(formData.vehicleType)}
                </dd>
              </div>
            </dl>
          </div>
          
          <div className="bg-neutral-50 p-4 rounded-md">
            <h4 className="font-medium text-neutral-900 border-b pb-2 mb-3">
              {t('vehicleDetails')}
            </h4>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
              <div className="col-span-1">
                <dt className="text-sm text-neutral-500">{t('manufacturer')}</dt>
                <dd className="text-sm font-medium">{formData.manufacturer}</dd>
              </div>
              <div className="col-span-1">
                <dt className="text-sm text-neutral-500">{t('model')}</dt>
                <dd className="text-sm font-medium">{formData.model}</dd>
              </div>
              <div className="col-span-1">
                <dt className="text-sm text-neutral-500">{t('registrationNumber')}</dt>
                <dd className="text-sm font-medium">{formData.registrationNumber}</dd>
              </div>
              <div className="col-span-1">
                <dt className="text-sm text-neutral-500">
                  {formData.vinCode ? t('vinCode') : t('frameNumber')}
                </dt>
                <dd className="text-sm font-medium">
                  {formData.vinCode || formData.frameNumber}
                </dd>
              </div>
            </dl>
          </div>
          
          <div className="bg-neutral-50 p-4 rounded-md">
            <h4 className="font-medium text-neutral-900 border-b pb-2 mb-3">
              {t('personalDetails')}
            </h4>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
              <div className="col-span-1">
                <dt className="text-sm text-neutral-500">{t('name')}</dt>
                <dd className="text-sm font-medium">{formData.firstName} {formData.lastName}</dd>
              </div>
              <div className="col-span-1">
                <dt className="text-sm text-neutral-500">{t('idNumber')}</dt>
                <dd className="text-sm font-medium">{formData.idNumber}</dd>
              </div>
              <div className="col-span-1">
                <dt className="text-sm text-neutral-500">{t('phone')}</dt>
                <dd className="text-sm font-medium">{formData.phone}</dd>
              </div>
              <div className="col-span-1">
                <dt className="text-sm text-neutral-500">{t('email')}</dt>
                <dd className="text-sm font-medium">{formData.email || '-'}</dd>
              </div>
            </dl>
          </div>
          
          <div className="bg-primary-50 p-4 rounded-md border border-primary-200">
            <h4 className="font-medium text-primary-800 flex items-center">
              <ShieldCheck className="h-5 w-5 mr-2" />
              {t('totalPrice')}
            </h4>
            <div className="mt-3 flex items-center">
              <span className="text-xl font-bold text-primary-800">
                {formData.duration === "15" && "$50"}
                {formData.duration === "30" && "$80"}
                {formData.duration === "90" && "$200"}
                {formData.duration === "365" && "$500"}
              </span>
            </div>
            <div className="mt-4">
              <div className="flex items-center mb-2">
                <input
                  type="radio"
                  id="cardPayment"
                  name="paymentMethod"
                  value="card"
                  checked={formData.paymentMethod === 'card'}
                  onChange={handleChange}
                  className="mr-2"
                />
                <label htmlFor="cardPayment" className="text-sm font-medium text-neutral-700">
                  {t('cardPayment')}
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="radio"
                  id="bankTransfer"
                  name="paymentMethod"
                  value="transfer"
                  checked={formData.paymentMethod === 'transfer'}
                  onChange={handleChange}
                  className="mr-2"
                />
                <label htmlFor="bankTransfer" className="text-sm font-medium text-neutral-700">
                  {t('bankTransfer')}
                </label>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
      <div className="p-6 bg-primary-800 text-white">
        <h2 className="text-2xl font-bold flex items-center">
          <ShieldCheck className="mr-3 h-6 w-6" />
          {t('vehicleInsurance')}
        </h2>
        <p className="mt-2 text-primary-200">
          {t('insuranceFormSubtitle')}
        </p>
      </div>
      
      {/* Step indicators - NOT clickable anymore */}
      <div className="px-6 py-4 bg-neutral-50 border-b border-neutral-200">
        <div className="flex items-center">
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              {/* Step circle */}
              <div 
                className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  activeStep === index 
                    ? 'bg-primary-600 text-white' 
                    : activeStep > index 
                      ? 'bg-success-600 text-white'
                      : 'bg-neutral-200 text-neutral-600'
                }`}
              >
                {activeStep > index ? (
                  <ShieldCheck className="h-5 w-5" />
                ) : (
                  React.createElement(steps[index].icon, { className: "h-4 w-4" })
                )}
              </div>
              
              {/* Step title */}
              <span 
                className={`ml-2 text-sm font-medium ${
                  activeStep === index ? 'text-primary-600' : 'text-neutral-500'
                }`}
              >
                {step.title}
              </span>
              
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="flex-1 border-t border-neutral-300 mx-4"></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      
      <form onSubmit={handleSubmit}>
        {/* Step content */}
        <div className="p-6">
          {steps[activeStep].content}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-neutral-50 border-t border-neutral-200 flex justify-between">
          <button
            type="button"
            onClick={handlePreviousStep}
            disabled={activeStep === 0 || isSubmitting}
            className={`px-4 py-2 border rounded-md ${
              activeStep === 0 || isSubmitting
                ? 'border-neutral-300 bg-neutral-100 text-neutral-400 cursor-not-allowed' 
                : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50'
            }`}
          >
            {t('previousStep')}
          </button>
          
          {activeStep < steps.length - 1 ? (
            <button
              type="button"
              onClick={handleNextStep}
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md"
            >
              {t('nextStep')}
            </button>
          ) : (
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-success-600 hover:bg-success-700 text-white rounded-md"
            >
              {isSubmitting ? (
                <>
                  <span className="mr-2">
                    <svg className="animate-spin h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                  {t('processing')}
                </>
              ) : (
                t('submitApplication')
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default InsuranceForm;